import { useCoachQuery } from '@/features/home/queries';
import { useSessionStore } from '@/stores/session-store';
import { getInactiveKeyFromNumber } from '@/utils/helper';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react';
import { useShallow } from 'zustand/react/shallow';

export type PlayerLastActiveFilter = '1_month' | '3_months' | '6_months' | '1_5_years' | 'show_all';

export type PlayerFiltersState = {
  level: number | null;
  lastActive: PlayerLastActiveFilter;
};

type PlayerFiltersActions = {
  setFilters: (next: Partial<PlayerFiltersState>) => void;
  reset: () => void;
};

type PlayerFiltersContextValue = PlayerFiltersState & PlayerFiltersActions;

const DEFAULT_FILTERS: PlayerFiltersState = {
  level: null,
  lastActive: 'show_all',
};

const PlayerFiltersContext = createContext<PlayerFiltersContextValue | null>(null);

export function PlayerFiltersProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PlayerFiltersState>(DEFAULT_FILTERS);
  const uid = useSessionStore(useShallow(_state => _state.user?.uid));
  const { data: coach } = useCoachQuery(uid);
  const coachInactiveFilterLevel = coach?.inactiveFilterLevel ?? 0;
  const setFilters = useCallback((next: Partial<PlayerFiltersState>) => {
    setState(prev => ({ ...prev, ...next }));
  }, []);

  const reset = useCallback(() => {
    setState(DEFAULT_FILTERS);
  }, []);

  const value = useMemo<PlayerFiltersContextValue>(
    () => ({
      ...state,
      setFilters,
      reset,
    }),
    [reset, setFilters, state],
  );

  const setFiltersFromCoach = useEffectEvent(() => {
    if (coachInactiveFilterLevel) {
      const key = getInactiveKeyFromNumber(coachInactiveFilterLevel);
      setFilters({ lastActive: key });
    } else {
      setFilters({ lastActive: 'show_all' });
    }
  });

  useEffect(() => {
    setFiltersFromCoach();
  }, [coachInactiveFilterLevel]);

  return <PlayerFiltersContext.Provider value={value}>{children}</PlayerFiltersContext.Provider>;
}

export function usePlayerFilters() {
  const ctx = useContext(PlayerFiltersContext);
  if (!ctx) {
    throw new Error('usePlayerFilters must be used within PlayerFiltersProvider');
  }
  return ctx;
}

export const PLAYER_FILTERS_DEFAULTS = DEFAULT_FILTERS;
